<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
        <div>
          <h1 class="h4 mb-1">Journées de caisse</h1>
          <small class="text-muted">
            Synthèse globale des ventes et du billetage par journée.
          </small>
        </div>
        <button type="button" class="btn btn-outline-secondary btn-sm btn-action-icon" title="Actualiser" @click="fetchSummary">
          <i class="fa fa-rotate"></i>
        </button>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>

      <div class="card module-filter-card mb-3" data-tour="day-summary-filters">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <div class="col-md-3">
              <label class="form-label small mb-1">Journée</label>
              <input
                v-model="filters.date"
                type="date"
                class="form-control form-control-sm"
                :max="todayDate"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label small mb-1">Caissier</label>
              <select v-model="filters.cashier_id" class="form-select form-select-sm" :disabled="isLoadingCashiers">
                <option value="">Tous les caissiers</option>
                <option v-for="cashier in cashiers" :key="cashier.id" :value="String(cashier.id)">
                  {{ cashier.name }}
                </option>
              </select>
            </div>
            <div class="col-12 module-filter-actions mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchSummary">
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="text-center py-5">
        <i class="fa fa-spinner fa-spin me-1"></i>
        Chargement...
      </div>

      <div v-else>
        <div class="row g-3 mb-3" data-tour="day-summary-totals">
          <div v-for="metric in summaryMetrics" :key="metric.key" class="col-sm-6 col-xl-3">
            <div class="card border-0 shadow-sm h-100 admin-day-metric">
              <div class="card-body">
                <div class="small text-muted mb-1">{{ metric.label }}</div>
                <div class="h5 mb-0" :class="metric.class">{{ metric.value }}</div>
                <small v-if="metric.hint" class="text-muted d-block mt-1">{{ metric.hint }}</small>
              </div>
            </div>
          </div>
        </div>

        <section class="admin-day-section mb-3" data-tour="day-summary-sessions">
          <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
            <div>
              <h2 class="h5 mb-1">Sessions de la journée</h2>
              <p class="text-muted mb-0">
                Caisses ouvertes sur la journée sélectionnée et reprises autorisées.
              </p>
            </div>
          </div>

          <BootstrapDataTable
            :rows="sessionRows"
            :columns="visibleSessionColumns"
            row-key="id"
            :client-pagination="false"
            :show-footer="false"
            :show-refresh="false"
            :show-export="false"
            :show-print="false"
            empty-text="Aucune session de caisse pour cette journée."
            @row-click="openSessionDetail"
          >
            <template #cell-cash_register="{ row }">
              {{ row.cash_register?.name || '-' }}
            </template>
            <template #cell-cashier="{ row }">
              {{ row.cashier?.name || '-' }}
            </template>
            <template #cell-opened_at="{ value }">
              {{ formatDateTime(value) }}
            </template>
            <template #cell-closed_at="{ value }">
              {{ formatDateTime(value) }}
            </template>
            <template #cell-status="{ row }">
              <span class="badge" :class="sessionStatusBadgeClass(row.status, row.is_historical_entry)">
                {{ sessionStatusLabel(row.status, row.is_historical_entry) }}
              </span>
            </template>
            <template #cell-cash_count="{ row }">
              <span class="badge" :class="cashCountBadgeClass(row.cash_count?.status)">
                {{ cashCountStatusLabel(row.cash_count?.status) }}
              </span>
            </template>
            <template #cell-active_access_grant="{ row }">
              <span v-if="row.active_access_grant" class="text-success fw-semibold">
                Jusqu’au {{ formatDateTime(row.active_access_grant.expires_at) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell-actions="{ row }">
              <div class="d-flex justify-content-end gap-2">
                <button
                  v-if="row.status === 'open' && !row.is_historical_entry"
                  type="button"
                  class="btn btn-outline-danger btn-sm btn-action-icon"
                  title="Clôturer la session"
                  :disabled="closingSessionId === row.id"
                  @click.stop="closeSession(row)"
                >
                  <i class="fa" :class="closingSessionId === row.id ? 'fa-spinner fa-spin' : 'fa-lock'"></i>
                </button>
                <template v-if="cashRegisterAutoCloseEnabled && !row.is_historical_entry">
                  <button
                    v-if="row.active_access_grant"
                    type="button"
                    class="btn btn-outline-danger btn-sm btn-action-icon"
                    title="Révoquer l’accès de reprise"
                    :disabled="grantDialog.isSubmitting"
                    @click.stop="revokeGrant(row)"
                  >
                    <i class="fa fa-ban"></i>
                  </button>
                  <button
                    v-else-if="row.can_grant_resume"
                    type="button"
                    class="btn btn-outline-primary btn-sm btn-action-icon"
                    title="Autoriser la reprise"
                    @click.stop="openGrantDialog(row)"
                  >
                    <i class="fa fa-clock-rotate-left"></i>
                  </button>
                  <span v-else class="text-muted">-</span>
                </template>
                <span v-else class="text-muted">-</span>
              </div>
            </template>
          </BootstrapDataTable>
        </section>

        <section v-if="hasSoldProducts" class="admin-day-section mb-3">
          <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
            <div>
              <h2 class="h5 mb-1">Produits vendus</h2>
              <p class="text-muted mb-0">
                Produits vendus sur la journée sélectionnée.
              </p>
            </div>
          </div>

          <BootstrapDataTable
            :rows="soldProducts"
            :columns="soldProductColumns"
            row-key="_row_key"
            :sort-by="soldProductsTable.sortBy"
            :sort-dir="soldProductsTable.sortDir"
            :per-page="soldProductsTable.perPage"
            searchable
            v-model:search-value="soldProductsSearch"
            search-placeholder="Rechercher un produit vendu..."
            export-file-name="produits-vendus-journee-caisse.xlsx"
            export-sheet-name="Produits vendus"
            print-title="Produits vendus"
            empty-text="Aucun produit vendu pour cette journée."
            @sort-change="handleSoldProductsSortChange"
            @refresh="fetchSummary"
          >
            <template #cell-quantity="{ value }">
              <span class="fw-semibold">{{ Number(value || 0) }}</span>
            </template>
            <template #cell-unit_price="{ value }">
              {{ $format.currency(value || 0) }}
            </template>
            <template #cell-total_amount="{ value }">
              <span class="fw-semibold">{{ $format.currency(value || 0) }}</span>
            </template>
          </BootstrapDataTable>
        </section>

        <section v-if="hasWalletTransactions" class="admin-day-section mb-3">
          <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
            <div>
              <h2 class="h5 mb-1">Transactions portefeuille</h2>
              <p class="text-muted mb-0">Opérations électroniques de la journée sélectionnée.</p>
            </div>
            <div class="d-flex gap-2 flex-wrap wallet-transaction-counters">
              <span class="badge bg-success">Recharges : {{ $format.currency(walletTotals.recharges || 0) }}</span>
              <span class="badge bg-primary">Dépôts : {{ $format.currency(walletTotals.deposits || 0) }}</span>
              <span class="badge bg-warning text-dark">Retraits : {{ $format.currency(walletTotals.withdrawals || 0) }}</span>
              <span class="badge bg-secondary">Ajustements : {{ $format.currency(walletTotals.adjustments || 0) }}</span>
              <span class="badge bg-danger">Annulations : {{ $format.currency(walletTotals.cancellations || 0) }}</span>
              <span class="badge bg-dark">Impact espèces : {{ $format.currency(walletTotals.cash_impact || 0) }}</span>
              <span class="badge bg-info text-dark">Marge estimée : {{ $format.currency(walletTotals.profit || 0) }}</span>
            </div>
          </div>

          <BootstrapDataTable
            :rows="walletTransactions"
            :columns="walletTransactionColumns"
            row-key="id"
            sort-by="created_at"
            sort-dir="desc"
            :per-page="10"
            searchable
            search-placeholder="Rechercher une transaction portefeuille..."
            export-file-name="transactions-portefeuille-journee-caisse.xlsx"
            export-sheet-name="Transactions portefeuille"
            print-title="Transactions portefeuille"
            empty-text="Aucune transaction portefeuille pour cette journée."
            @refresh="fetchSummary"
          >
            <template #cell-created_at="{ value }">{{ formatDateTime(value) }}</template>
            <template #cell-type="{ value }">{{ walletOperationLabel(value) }}</template>
            <template #cell-amount="{ value }">
              <span class="fw-semibold">{{ $format.currency(value || 0) }}</span>
            </template>
            <template #cell-profit_percentage="{ value }">{{ profitPercentageLabel(value) }}</template>
            <template #cell-profit_amount="{ value }">{{ profitAmountLabel(value) }}</template>
            <template #cell-balance_after="{ value }">{{ $format.currency(value || 0) }}</template>
            <template #cell-status="{ value }">
              <span class="badge" :class="walletStatusBadgeClass(value)">
                {{ walletStatusLabel(value) }}
              </span>
            </template>
          </BootstrapDataTable>
        </section>
      </div>

      <Dialog
        v-model:visible="sessionDetailDialog.visible"
        :modal="true"
        :style="{ width: '820px', maxWidth: '96vw' }"
        header="Détail de la session et du billetage"
      >
        <div v-if="sessionDetailDialog.session" class="session-detail-dialog">
          <div class="row g-3 mb-3">
            <div class="col-sm-6 col-lg-3">
              <div class="small text-muted">Caisse</div>
              <div class="fw-semibold">{{ sessionDetailDialog.session.cash_register?.name || '-' }}</div>
            </div>
            <div class="col-sm-6 col-lg-3">
              <div class="small text-muted">Caissier</div>
              <div class="fw-semibold">{{ sessionDetailDialog.session.cashier?.name || '-' }}</div>
            </div>
            <div class="col-sm-6 col-lg-3">
              <div class="small text-muted">Ouverture</div>
              <div class="fw-semibold">{{ formatDateTime(sessionDetailDialog.session.opened_at) }}</div>
            </div>
            <div class="col-sm-6 col-lg-3">
              <div class="small text-muted">Fermeture</div>
              <div class="fw-semibold">{{ formatDateTime(sessionDetailDialog.session.closed_at) }}</div>
            </div>
          </div>

          <div v-if="selectedSessionCashCount">
            <div class="row g-3 mb-3">
              <div class="col-sm-6 col-lg-3">
                <div class="small text-muted">Statut session</div>
                <span class="badge" :class="sessionStatusBadgeClass(sessionDetailDialog.session.status)">
                  {{ sessionStatusLabel(sessionDetailDialog.session.status) }}
                </span>
              </div>
              <div class="col-sm-6 col-lg-3">
                <div class="small text-muted">Statut billetage</div>
                <span class="badge" :class="cashCountBadgeClass(selectedSessionCashCount.status)">
                  {{ cashCountStatusLabel(selectedSessionCashCount.status) }}
                </span>
              </div>
              <div class="col-sm-6 col-lg-2">
                <div class="small text-muted">Théorique</div>
                <div class="fw-semibold">{{ $format.currency(selectedSessionCashCount.expected_amount || 0) }}</div>
              </div>
              <div class="col-sm-6 col-lg-2">
                <div class="small text-muted">Compté</div>
                <div class="fw-semibold">{{ $format.currency(selectedSessionCashCount.counted_amount || 0) }}</div>
              </div>
              <div class="col-sm-6 col-lg-2">
                <div class="small text-muted">Différence</div>
                <div
                  class="fw-semibold"
                  :class="{
                    'text-success': Number(selectedSessionCashCount.difference || 0) > 0,
                    'text-danger': Number(selectedSessionCashCount.difference || 0) < 0,
                  }"
                >
                  {{ $format.currency(selectedSessionCashCount.difference || 0) }}
                </div>
              </div>
            </div>

            <div v-if="selectedSessionCashCountComment" class="alert alert-light border mb-3">
              <div class="small text-muted fw-semibold mb-1">Commentaire</div>
              <div>{{ selectedSessionCashCountComment }}</div>
            </div>

            <div class="table-responsive">
              <table class="table table-sm table-striped table-bordered align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Libellé</th>
                    <th class="text-end">Valeur faciale</th>
                    <th class="text-end">Quantité</th>
                    <th class="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="line in selectedSessionCashCountLines" :key="line.id || `${line.label}-${line.denomination}`">
                    <td>{{ line.label || '-' }}</td>
                    <td class="text-end">{{ $format.currency(line.denomination || 0) }}</td>
                    <td class="text-end">{{ Number(line.quantity || 0) }}</td>
                    <td class="text-end">{{ $format.currency(line.line_total || 0) }}</td>
                  </tr>
                  <tr v-if="!selectedSessionCashCountLines.length">
                    <td colspan="4" class="text-center text-muted py-3">
                      Aucune ligne de billetage renseignée.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else class="alert alert-light border text-muted mb-0">
            Aucun billetage enregistré pour cette session.
          </div>
        </div>

        <template #footer>
          <button type="button" class="btn btn-outline-secondary" @click="closeSessionDetail">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Fermer
          </button>
        </template>
      </Dialog>

      <Dialog
        v-model:visible="grantDialog.visible"
        :modal="true"
        :style="{ width: '520px', maxWidth: '100%' }"
        header="Autoriser une reprise"
      >
        <div v-if="grantDialog.session" class="grant-dialog-body">
          <p class="text-muted mb-3">
            La caissière pourra ajouter des ventes et compléter les brouillons dans cette session fermée.
            La session restera fermée dans l’historique.
          </p>

          <div v-if="grantDialog.error" class="alert alert-danger py-2">
            {{ grantDialog.error }}
          </div>

          <div class="mb-3">
            <label class="form-label">Session</label>
            <div class="form-control-plaintext py-0">
              {{ grantDialogSessionLabel }}
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Durée d’accès</label>
            <select v-model.number="grantDialog.duration_minutes" class="form-select">
              <option :value="30">30 minutes</option>
              <option :value="60">1 heure</option>
              <option :value="120">2 heures</option>
              <option :value="240">4 heures</option>
              <option :value="480">8 heures</option>
            </select>
          </div>

          <div class="mb-0">
            <label class="form-label">Motif</label>
            <textarea
              v-model.trim="grantDialog.reason"
              class="form-control"
              rows="3"
              maxlength="500"
              placeholder="Ex. coupure réseau, vente non terminée..."
            ></textarea>
          </div>
        </div>

        <template #footer>
          <button type="button" class="btn btn-outline-secondary" :disabled="grantDialog.isSubmitting" @click="closeGrantDialog">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Annuler
          </button>
          <button type="button" class="btn btn-primary" :disabled="grantDialog.isSubmitting" @click="submitGrant">
            <i class="fa me-1" :class="grantDialog.isSubmitting ? 'fa-spinner fa-spin' : 'fa-check'"></i>
            Autoriser
          </button>
        </template>
      </Dialog>
    </template>
  </AdminLayout>
</template>

<script>
import Dialog from 'primevue/dialog';
import { showAppToast } from '@/support/toastBus';

function formatLocalDate(date) {
  const pad = (number) => String(number).padStart(2, '0');

  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join('-');
}

const defaultFilters = () => ({
  date: formatLocalDate(new Date()),
  cashier_id: '',
});

export default {
  name: 'AdminDaySummaryView',
  components: {
    Dialog,
  },
  data() {
    return {
      filters: defaultFilters(),
      summary: {
        totals: {},
        sessions: [],
        sold_products: [],
        wallet_transactions: [],
        wallet_totals: {},
      },
      cashiers: [],
      isLoading: false,
      isLoadingCashiers: false,
      errorMessage: '',
      soldProductsSearch: '',
      closingSessionId: null,
      sessionDetailDialog: {
        visible: false,
        session: null,
      },
      soldProductsTable: {
        sortBy: 'name',
        sortDir: 'asc',
        perPage: 10,
      },
      grantDialog: {
        visible: false,
        session: null,
        duration_minutes: 60,
        reason: '',
        error: '',
        isSubmitting: false,
      },
    };
  },
  computed: {
    shopInfo() {
      return this.$store?.getters?.['shop/shopInfo'] || {};
    },
    cashRegisterAutoCloseEnabled() {
      return this.shopInfo.cash_register_auto_close_enabled === true;
    },
    todayDate() {
      return formatLocalDate(new Date());
    },
    totals() {
      return this.summary.totals || {};
    },
    summaryMetrics() {
      return [
        {
          key: 'sales_count',
          label: 'Ventes validées',
          value: Number(this.totals.sales_count || 0).toLocaleString('fr-FR'),
        },
        {
          key: 'sales_amount',
          label: 'Chiffre d’affaires',
          value: this.$format.currency(this.totals.sales_amount || 0),
        },
        {
          key: 'cash_sales_amount',
          label: 'Espèces encaissées',
          value: this.$format.currency(this.totals.cash_sales_amount || 0),
        },
        {
          key: 'credit_sales_amount',
          label: 'Ventes à crédit',
          value: this.$format.currency(this.totals.credit_sales_amount || 0),
        },
        {
          key: 'card_sales_amount',
          label: 'Carte',
          value: this.$format.currency(this.totals.card_sales_amount || 0),
        },
        {
          key: 'mobile_money_sales_amount',
          label: 'Mobile Money',
          value: this.$format.currency(this.totals.mobile_money_sales_amount || 0),
        },
        {
          key: 'opening_amount',
          label: 'Fonds initiaux',
          value: this.$format.currency(this.totals.opening_amount || 0),
        },
        {
          key: 'expected_amount',
          label: 'Montant théorique',
          value: this.$format.currency(this.totals.expected_amount || 0),
        },
        {
          key: 'counted_amount',
          label: 'Montant compté',
          value: this.$format.currency(this.totals.counted_amount || 0),
        },
        {
          key: 'difference',
          label: 'Écart billetage',
          value: this.$format.currency(this.totals.difference || 0),
          class: {
            'text-success': Number(this.totals.difference || 0) > 0,
            'text-danger': Number(this.totals.difference || 0) < 0,
          },
        },
        {
          key: 'pending_sales_count',
          label: 'Ventes en attente',
          value: Number(this.totals.pending_sales_count || 0).toLocaleString('fr-FR'),
        },
        {
          key: 'cancelled_sales_count',
          label: 'Ventes annulées',
          value: Number(this.totals.cancelled_sales_count || 0).toLocaleString('fr-FR'),
        },
        {
          key: 'sessions_count',
          label: 'Sessions de caisse',
          value: Number(this.totals.sessions_count || 0).toLocaleString('fr-FR'),
          hint: `${Number(this.totals.open_sessions_count || 0)} ouverte(s), ${Number(this.totals.closed_sessions_count || 0)} fermée(s)`,
        },
        {
          key: 'cash_counts_count',
          label: 'Billetages',
          value: Number(this.totals.cash_counts_count || 0).toLocaleString('fr-FR'),
          hint: `${Number(this.totals.validated_cash_counts_count || 0)} validé(s), ${Number(this.totals.draft_cash_counts_count || 0)} brouillon(s)`,
        },
      ];
    },
    soldProductColumns() {
      return [
        { key: 'name', label: 'Produit', sortable: true },
        { key: 'quantity', label: 'Qté', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'unit_price', label: 'Prix unitaire', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'total_amount', label: 'Total vente', sortable: true, class: 'text-end', headerClass: 'text-end' },
      ];
    },
    sessionColumns() {
      return [
        { key: 'cash_register', label: 'Caisse' },
        { key: 'cashier', label: 'Caissier' },
        { key: 'opened_at', label: 'Ouverture' },
        { key: 'closed_at', label: 'Fermeture' },
        { key: 'status', label: 'Statut' },
        { key: 'cash_count', label: 'Billetage' },
        { key: 'active_access_grant', label: 'Reprise' },
        { key: 'actions', label: '', class: 'text-end', headerClass: 'text-end' },
      ];
    },
    visibleSessionColumns() {
      if (this.cashRegisterAutoCloseEnabled) {
        return this.sessionColumns;
      }

      return this.sessionColumns.filter((column) => column.key !== 'active_access_grant');
    },
    walletTransactionColumns() {
      return [
        { key: 'created_at', label: 'Date', sortable: true },
        { key: 'reference', label: 'Référence', sortable: true },
        { key: 'customer_phone', label: 'Numéro client' },
        { key: 'operator_reference', label: 'Réf. opérateur' },
        { key: 'wallet.name', label: 'Portefeuille', sortable: true },
        { key: 'type', label: 'Opération', sortable: true },
        { key: 'amount', label: 'Montant', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'profit_percentage', label: 'Taux bénéfice', class: 'text-end', headerClass: 'text-end' },
        { key: 'profit_amount', label: 'Marge estimée', class: 'text-end', headerClass: 'text-end' },
        { key: 'balance_after', label: 'Solde après', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'user.name', label: 'Utilisateur', sortable: true },
        { key: 'cash_register.name', label: 'Caisse', sortable: true },
        { key: 'status', label: 'Statut', sortable: true },
      ];
    },
    sessionRows() {
      return Array.isArray(this.summary.sessions) ? this.summary.sessions : [];
    },
    selectedSessionCashCount() {
      return this.sessionDetailDialog.session?.cash_count || null;
    },
    selectedSessionCashCountComment() {
      return String(this.selectedSessionCashCount?.comment || '').trim();
    },
    selectedSessionCashCountLines() {
      return Array.isArray(this.selectedSessionCashCount?.lines)
        ? this.selectedSessionCashCount.lines.filter((line) => Number(line.quantity || 0) > 0)
        : [];
    },
    grantDialogSessionLabel() {
      const session = this.grantDialog.session;
      if (!session) return '';

      const register = session.cash_register?.name || 'Caisse';
      const cashier = session.cashier?.name || 'caissier';
      const openedAt = this.formatDateTime(session.opened_at);

      return `${register} - ${cashier} - ${openedAt}`;
    },
    soldProductRows() {
      return Array.isArray(this.summary.sold_products)
        ? this.summary.sold_products.map((product, index) => ({
          ...product,
          _row_key: [
            product.product_id ?? 'none',
            product.name || 'product',
            product.unit_price ?? 0,
            index,
          ].join('-'),
        }))
        : [];
    },
    soldProducts() {
      return this.soldProductRows.filter((product) => this.matchesSearch(product, this.soldProductsSearch, [
        'name',
        'quantity',
        'unit_price',
        'total_amount',
      ]));
    },
    hasSoldProducts() {
      return this.soldProductRows.length > 0;
    },
    walletTransactions() {
      return Array.isArray(this.summary.wallet_transactions) ? this.summary.wallet_transactions : [];
    },
    isWalletEnabled() {
      return this.$store?.getters?.['runtime/moduleEnabled']?.('wallet') !== false;
    },
    hasWalletTransactions() {
      return this.isWalletEnabled && this.walletTransactions.length > 0;
    },
    walletTotals() {
      return this.summary.wallet_totals || {};
    },
  },
  mounted() {
    if (this.$route?.query?.date && String(this.$route.query.date) <= this.todayDate) {
      this.filters.date = String(this.$route.query.date);
    }
    this.fetchCashiers();
    this.fetchSummary();
  },
  methods: {
    formatDate(value) {
      return value ? this.$format.date(value) : '-';
    },
    async fetchCashiers() {
      this.isLoadingCashiers = true;
      try {
        const response = await this.$api.get('/users', { params: { role: 'caissier' } });
        const users = response.data.data || response.data || [];
        this.cashiers = Array.isArray(users)
          ? users.map((user) => ({ id: user.id, name: user.name }))
          : [];
      } catch (error) {
        this.cashiers = [];
      } finally {
        this.isLoadingCashiers = false;
      }
    },
    async fetchSummary() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const params = {
          date: this.filters.date,
        };
        if (this.filters.cashier_id) {
          params.cashier_id = this.filters.cashier_id;
        }

        const response = await this.$api.get('/cash-counts/admin-day-summary', { params });
        this.summary = response.data.data || response.data || {};
      } catch (error) {
        this.errorMessage = 'Impossible de charger le résumé de la journée.';
      } finally {
        this.isLoading = false;
      }
    },
    resetFilters() {
      this.filters = defaultFilters();
      this.fetchSummary();
    },
    formatDateTime(value) {
      return value ? this.$format.datetime(value) : '-';
    },
    sessionStatusLabel(status, isHistoricalEntry = false) {
      if (isHistoricalEntry) return 'Rattrapage archivé';
      return status === 'open' ? 'Ouverte' : 'Fermée';
    },
    sessionStatusBadgeClass(status, isHistoricalEntry = false) {
      if (isHistoricalEntry) return 'bg-dark';
      return status === 'open' ? 'bg-success' : 'bg-secondary';
    },
    cashCountStatusLabel(status) {
      if (status === 'validated') return 'Validé';
      if (status === 'draft') return 'Brouillon';

      return 'Aucun';
    },
    cashCountBadgeClass(status) {
      if (status === 'validated') return 'bg-success';
      if (status === 'draft') return 'bg-warning text-dark';

      return 'bg-light text-muted border';
    },
    handleSoldProductsSortChange({ sortBy, sortDir }) {
      this.soldProductsTable.sortBy = sortBy;
      this.soldProductsTable.sortDir = sortDir;
    },
    walletOperationLabel(type) {
      return ({
        recharge: 'Recharge',
        deposit: 'Dépôt',
        withdrawal: 'Retrait',
        adjustment: 'Ajustement',
        cancellation: 'Annulation',
      })[type] || type || '—';
    },
    profitPercentageLabel(value) {
      return value === null || value === undefined || value === ''
        ? 'Non renseigné'
        : `${Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`;
    },
    profitAmountLabel(value) {
      return value === null || value === undefined || value === ''
        ? '—'
        : this.$format.currency(value);
    },
    walletStatusLabel(status) {
      if (status === 'validated') return 'Validée';
      if (status === 'cancelled') return 'Annulée';

      const label = String(status || 'Inconnu').replace(/[_-]+/g, ' ');
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    walletStatusBadgeClass(status) {
      if (status === 'validated') return 'bg-success';
      if (status === 'cancelled') return 'bg-danger';

      return 'bg-secondary';
    },
    matchesSearch(row, search, fields) {
      const keyword = String(search || '').trim().toLowerCase();
      if (!keyword) return true;

      return fields.some((field) => String(row?.[field] ?? '').toLowerCase().includes(keyword));
    },
    openSessionDetail(session) {
      this.sessionDetailDialog = {
        visible: true,
        session,
      };
    },
    closeSessionDetail() {
      this.sessionDetailDialog.visible = false;
      this.sessionDetailDialog.session = null;
    },
    openGrantDialog(session) {
      if (!this.cashRegisterAutoCloseEnabled) return;

      this.grantDialog = {
        visible: true,
        session,
        duration_minutes: 60,
        reason: '',
        error: '',
        isSubmitting: false,
      };
    },
    closeGrantDialog() {
      if (this.grantDialog.isSubmitting) return;

      this.grantDialog.visible = false;
      this.grantDialog.session = null;
      this.grantDialog.reason = '';
      this.grantDialog.error = '';
    },
    async submitGrant() {
      const session = this.grantDialog.session;
      if (!this.cashRegisterAutoCloseEnabled || !session?.id || this.grantDialog.isSubmitting) return;

      if (!String(this.grantDialog.reason || '').trim()) {
        this.grantDialog.error = 'Indiquez le motif de la reprise.';
        return;
      }

      this.grantDialog.isSubmitting = true;
      this.grantDialog.error = '';

      try {
        await this.$api.post(`/cash-register-sessions/${session.id}/access-grants`, {
          duration_minutes: Number(this.grantDialog.duration_minutes || 60),
          reason: this.grantDialog.reason,
        });
        showAppToast({ type: 'success', message: 'Reprise autorisée.' });
        this.grantDialog.isSubmitting = false;
        this.closeGrantDialog();
        await this.fetchSummary();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.grantDialog.error = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible d’autoriser cette reprise.';
      } finally {
        this.grantDialog.isSubmitting = false;
      }
    },
    async revokeGrant(session) {
      const grantId = session?.active_access_grant?.id;
      if (!grantId) return;

      const confirmed = await this.$confirmAction({
        message: 'Révoquer cette autorisation empêchera la caissière de reprendre cette session fermée.',
        header: 'Révoquer la reprise',
        icon: 'fa fa-ban text-danger',
        acceptLabel: 'Révoquer',
        rejectLabel: 'Annuler',
        acceptClass: 'p-button-danger',
        rejectClass: 'p-button-text',
      });

      if (!confirmed) return;

      try {
        await this.$api.delete(`/cash-register-sessions/access-grants/${grantId}`);
        showAppToast({ type: 'success', message: 'Autorisation révoquée.' });
        await this.fetchSummary();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible de révoquer cette autorisation.';
      }
    },
    async closeSession(session) {
      if (!session?.id || session.status !== 'open' || this.closingSessionId) return;

      const confirmed = await this.$confirmAction({
        message: 'Clôturer cette session maintenant ? La caissière ne pourra plus enregistrer de ventes et ses ventes brouillon seront abandonnées. Le billetage ne sera pas créé automatiquement.',
        header: 'Clôturer la session',
        icon: 'fa fa-lock text-danger',
        acceptLabel: 'Clôturer',
        rejectLabel: 'Annuler',
        acceptClass: 'p-button-danger',
        rejectClass: 'p-button-text',
      });

      if (!confirmed) return;

      this.closingSessionId = session.id;
      this.errorMessage = '';

      try {
        await this.$api.post(`/cash-register-sessions/${session.id}/close`);
        showAppToast({ type: 'success', message: 'Session de caisse fermée.' });
        await this.fetchSummary();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible de clôturer cette session.';
      } finally {
        this.closingSessionId = null;
      }
    },
  },
};
</script>

<style scoped>
.admin-day-metric .h5 {
  font-weight: 800;
}

.wallet-transaction-counters .badge {
  font-size: 0.85rem;
  padding: 0.45rem 0.65rem;
}

.grant-dialog-body {
  color: #0f172a;
}
</style>
